import useTranslation from 'next-translate/useTranslation';

// Third-party
import clsx from 'clsx';
import { includes } from 'lodash';

// Hooks
import { useSocialInboxNavigation } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxNavigation';
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import useToggle from '@/utils/hooks/utils/useToggle';

import type { SocialInboxType } from '@/components/pages/conversations/SocialInbox/types';

// Types/Libs
import pageRights from '@/lib/acl/pageRights';
import { MANAGE_DM_TRACKER } from '@/lib/routes/privateRoutes';
import { INSTAGRAM } from '@/lib/socialProfile';
import {
    DEFAULT_SOCIAL_INBOX_TYPES,
    INSTAGRAM_ONLY_FEATURES,
    SOCIAL_INBOX_MANAGING_OPTIONS
} from '@/components/pages/conversations/SocialInbox/const';

// Components
import Dropdown, { BaseItem } from '@/components/base/Dropdown';
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';
import { UpsellCrownIcon } from '@/components/composite/Upsell/UpsellCrownIcon/UpsellCrownIcon';
import {
    SocialInboxTitle,
    SocialInboxTypeTitle
} from '@/components/pages/conversations/SocialInbox';
import { DirectMessagesUpsellModal } from '@/components/pages/conversations/SocialInbox/components/DirectMessagesUpsellModal';

export const SocialInboxMenuMobile = () => {
    const { t } = useTranslation('conversations');
    const { activeSocialProfile } = useActiveSocialProfile();
    const { currentSocialInbox, navigateTo } = useSocialInboxNavigation();
    const { subscription } = useSubscription();
    const { isOpen: isUpsellModalOpen, toggle: toggleUpsellModal } = useToggle();

    const isInstagram = activeSocialProfile?.social_network_type === INSTAGRAM;
    const planHasAccessToDMs = pageRights[MANAGE_DM_TRACKER]?.plans?.includes(
        subscription?.product_key
    );

    const isMenuOptionDisabled = (socialInboxType: SocialInboxType) => {
        const isInstagramOnlyFeature = includes(INSTAGRAM_ONLY_FEATURES, socialInboxType);
        return isInstagramOnlyFeature && !isInstagram;
    };

    const renderUpsellCrown = (socialInboxType: SocialInboxType) => {
        const isDirectMessages = socialInboxType === 'dm';

        if (isInstagram && isDirectMessages && !planHasAccessToDMs) {
            return <UpsellCrownIcon />;
        }
    };

    const renderDropDownOption = (socialInboxType: SocialInboxType) => {
        const isDirectMessages = socialInboxType === 'dm';

        const handleClick = () => {
            if (isDirectMessages && !planHasAccessToDMs) {
                toggleUpsellModal();
            } else {
                navigateTo(socialInboxType);
            }
        };

        return (
            <Dropdown.Item
                key={socialInboxType}
                onClick={handleClick}
                disabled={isMenuOptionDisabled(socialInboxType)}>
                {({ active, disabled }) => (
                    <Tooltip
                        content={t('social_inbox.not_yet_available')}
                        interactive={true}
                        appendTo={() => document.body}
                        placement="right"
                        disabled={!disabled}>
                        <span className="z-20 flex items-center">
                            <BaseItem
                                active={active}
                                disabled={disabled}
                                className={clsx('flex items-center gap-2', {
                                    'cursor-not-allowed opacity-50': disabled
                                })}
                                key={socialInboxType}>
                                <SocialInboxTypeTitle type={socialInboxType} />

                                {renderUpsellCrown(socialInboxType)}
                            </BaseItem>
                        </span>
                    </Tooltip>
                )}
            </Dropdown.Item>
        );
    };

    return (
        <div
            data-testid="social-inbox-social-inbox-mobile-menu"
            className="w-full space-y-3 bg-white p-5 dark:bg-icoDarkMode-deepBlack">
            <SocialInboxTitle />

            <Dropdown
                hasBorder={true}
                classNameWrapper="w-full"
                className="!z-15"
                size="full"
                button={
                    <Dropdown.Button className="flex w-full items-center justify-between bg-icoBlue-100 px-5 py-4 text-icoBlue dark:bg-icoDarkMode-beton">
                        <SocialInboxTypeTitle
                            type={currentSocialInbox}
                            selected
                            classNameTextSize="text-16"
                        />
                        <Icon icon="chevron-bottom " className="text-16" />
                    </Dropdown.Button>
                }>
                <Dropdown.Divider>
                    {[...DEFAULT_SOCIAL_INBOX_TYPES, ...SOCIAL_INBOX_MANAGING_OPTIONS]
                        .filter((e) => e !== currentSocialInbox)
                        .map(renderDropDownOption)}
                </Dropdown.Divider>
            </Dropdown>

            <DirectMessagesUpsellModal isOpen={isUpsellModalOpen} toggle={toggleUpsellModal} />
        </div>
    );
};
